import type { MediaDto, ProductDto } from '@studio/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Package } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ImageSlots } from '@/components/ImageSlots';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input, Textarea } from '@/components/ui/form';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useProducts } from '@/lib/queries';

interface ImportStatus {
  running: boolean;
  result: { products: number; created: number; images: number } | null;
  error: string | null;
}
import { ago } from '@/lib/utils';

function ProductDialog({ product, onClose }: { product: ProductDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState<MediaDto[]>([]);
  const [promptDescription, setPromptDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [claims, setClaims] = useState('');
  useEffect(() => {
    if (!product) return;
    setLabel(product.labelImage ? [product.labelImage] : []);
    setPromptDescription(product.promptDescription);
    setNotes(product.notes);
    setClaims(product.allowedClaims);
  }, [product]);
  const save = useMutation({
    mutationFn: () =>
      api(`/api/products/${product!.id}`, {
        method: 'PATCH',
        body: { labelImageId: label[0]?.id ?? null, promptDescription, notes, allowedClaims: claims },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product saved');
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={!!product} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{product?.title}</DialogTitle>
          <DialogDescription>
            Store data comes from the shop; the fields below are the studio's own and stay when you import again.
          </DialogDescription>
        </DialogHeader>
        {product && (
          <div className="grid gap-4">
            <div className="flex flex-wrap gap-1">
              {product.variants.map((v) => (
                <Badge key={v.id} variant="secondary">
                  {v.title}: ₹{v.price}
                </Badge>
              ))}
            </div>
            {product.images.length > 0 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {product.images.map((m) => (
                  <img key={m.id} src={m.url} alt="" className="h-24 rounded-md border object-cover" />
                ))}
              </div>
            )}
            <div>
              <div className="mb-1.5 text-sm font-medium">Bottle / label picture for videos</div>
              <ImageSlots images={label} max={1} onChange={setLabel} />
              <p className="mt-1 text-xs text-muted-foreground">
                A clear picture of the pack on a plain background works best. Blank: the first store picture is used.
              </p>
            </div>
            <Field
              label="How prompts describe it (English)"
              hint='e.g. the white plastic supplement bottle with a green leaf label reading "Fat Cutter GREEN TEA Tablets"'
            >
              {(p) => (
                <Textarea
                  {...p}
                  rows={2}
                  value={promptDescription}
                  onChange={(e) => setPromptDescription(e.target.value)}
                />
              )}
            </Field>
            <Field label="Allowed claims (one per line)" hint="The storyboard AI may only use these claims.">
              {(p) => <Textarea {...p} rows={3} value={claims} onChange={(e) => setClaims(e.target.value)} />}
            </Field>
            <Field label="Notes">
              {(p) => <Textarea {...p} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />}
            </Field>
            <details className="text-sm">
              <summary className="cursor-pointer font-medium">Store description</summary>
              <p className="mt-2 whitespace-pre-wrap text-muted-foreground">{product.description || '—'}</p>
            </details>
          </div>
        )}
        <DialogFooter>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ProductsPage() {
  const qc = useQueryClient();
  const q = useProducts();
  const [editing, setEditing] = useState<ProductDto | null>(null);
  const [filter, setFilter] = useState('');
  // The import runs on the server in the background (hundreds of pictures); this follows it.
  const status = useQuery({
    queryKey: ['products-import'],
    queryFn: () => api<ImportStatus>('/api/products/import'),
    refetchInterval: (s) => (s.state.data?.running ? 2000 : false),
  });
  const wasRunning = useRef(false);
  useEffect(() => {
    const s = status.data;
    if (!s) return;
    if (wasRunning.current && !s.running) {
      if (s.error) toast.error(s.error);
      else if (s.result)
        toast.success(
          `${s.result.products} products read from the store (${s.result.created} new, ${s.result.images} pictures downloaded)`,
        );
      void qc.invalidateQueries({ queryKey: ['products'] });
    }
    if (s.running) void qc.invalidateQueries({ queryKey: ['products'] });
    wasRunning.current = s.running;
  }, [status.data, qc]);
  const importer = useMutation({
    mutationFn: () => api<ImportStatus>('/api/products/import', { method: 'POST' }),
    onSuccess: (s) => qc.setQueryData(['products-import'], s),
    onError: (e) => toast.error(errorMessage(e)),
  });
  const importing = importer.isPending || !!status.data?.running;
  const list = q.data?.filter((p) => p.title.toLowerCase().includes(filter.toLowerCase())) ?? [];
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Products"
        description="From the Herbal Cure store (its public product list; nothing is changed in the store)."
        actions={
          <Button onClick={() => importer.mutate()} loading={importing}>
            {!importing && <Download />} {importing ? 'Importing products and pictures…' : 'Import from Shopify'}
          </Button>
        }
      />
      <Input
        placeholder="Search products"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="max-w-xs"
      />
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && <Skeleton className="h-40" />}
      {q.data && !q.data.length && (
        <EmptyState icon={<Package />} title="No products yet" description='Press "Import from Shopify".' />
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((p) => (
          <Card key={p.id} className="cursor-pointer p-3 hover:bg-accent/40" onClick={() => setEditing(p)}>
            <div className="flex gap-3">
              <div className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                {p.referenceImage ? (
                  <img src={p.referenceImage.url} alt="" className="size-full object-cover" />
                ) : (
                  <Package className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 space-y-1">
                <div className="line-clamp-2 text-sm font-medium">{p.title}</div>
                <div className="text-xs text-muted-foreground">
                  {p.variants.map((v) => `₹${Number(v.price).toFixed(0)}`).join(' · ')}
                </div>
                <div className="flex flex-wrap gap-1">
                  {p.labelImage && <Badge variant="success">label picture</Badge>}
                  {p.promptDescription && <Badge variant="secondary">described</Badge>}
                  {!p.shopifyId && <Badge variant="warning">not imported</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {p.importedAt ? `imported ${ago(p.importedAt)}` : ''}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <ProductDialog product={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
